const CHECKOUT_STORAGE_KEY = 'checkoutSlotPreference';
const MAX_AGE_SECONDS = 3600;
const MAX_AGE_MS = MAX_AGE_SECONDS * 1000;
const STALENESS_THRESHOLD_MS = 300_000;

export interface SlotPreference {
  routeId: string;
  optionId: string;
  utcStartTime: number;
  activityType: string;
  displayDate: string;
  displayTime: string;
  displayEndRange: string;
  savedAt: number;
}

export interface SlotPreferenceInput {
  routeId: string;
  optionId: string;
  utcStartTime: number;
  activityType: string;
  displayDate: string;
  displayTime: string;
  displayEndRange: string;
}

export interface SlotPreferenceScope {
  userId?: string | null;
  vehicleId?: number | string | null;
  purchaseStartedAt?: string | null;
}

interface ScopedSlotPreference extends SlotPreference {
  userId: string;
  vehicleId: string;
}

interface CompleteSlotPreferenceScope extends SlotPreferenceScope {
  userId: string;
  vehicleId: number | string;
}

const hasCompleteScope = (
  scope: SlotPreferenceScope | undefined
): scope is CompleteSlotPreferenceScope => Boolean(scope?.userId && scope.vehicleId != null);

const getStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const parsePurchaseStartedAt = (purchaseStartedAt?: string | null) => {
  if (!purchaseStartedAt) return null;
  const purchaseStartedAtMs = Date.parse(purchaseStartedAt);
  return Number.isNaN(purchaseStartedAtMs) ? null : purchaseStartedAtMs;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isScopedSlotPreference = (value: unknown): value is ScopedSlotPreference => {
  if (!isRecord(value)) return false;

  return (
    typeof value.routeId === 'string' &&
    typeof value.optionId === 'string' &&
    typeof value.utcStartTime === 'number' &&
    typeof value.activityType === 'string' &&
    typeof value.displayDate === 'string' &&
    typeof value.displayTime === 'string' &&
    typeof value.displayEndRange === 'string' &&
    typeof value.savedAt === 'number' &&
    typeof value.userId === 'string' &&
    typeof value.vehicleId === 'string'
  );
};

const isScopedPreferenceValid = (
  pref: ScopedSlotPreference,
  scope: CompleteSlotPreferenceScope
) => {
  if (typeof pref.savedAt !== 'number') return false;
  const scopeVehicleId = String(scope.vehicleId);
  if (pref.userId !== scope.userId || pref.vehicleId !== scopeVehicleId) return false;
  if (Date.now() - pref.savedAt > MAX_AGE_MS) return false;

  const purchaseStartedAtMs = parsePurchaseStartedAt(scope.purchaseStartedAt);
  if (purchaseStartedAtMs != null && pref.savedAt < purchaseStartedAtMs) return false;

  return true;
};

export function getScopedSlotPreference(
  scope: SlotPreferenceScope | undefined
): SlotPreference | undefined {
  if (!hasCompleteScope(scope)) return undefined;

  const storage = getStorage();
  if (!storage) return undefined;

  const raw = storage.getItem(CHECKOUT_STORAGE_KEY);
  if (!raw) return undefined;

  try {
    const pref: unknown = JSON.parse(raw);
    if (!isScopedSlotPreference(pref) || !isScopedPreferenceValid(pref, scope)) {
      storage.removeItem(CHECKOUT_STORAGE_KEY);
      return undefined;
    }

    return pref;
  } catch {
    storage.removeItem(CHECKOUT_STORAGE_KEY);
    return undefined;
  }
}

export function hasScopedSlotPreference(scope: SlotPreferenceScope | undefined): boolean {
  return getScopedSlotPreference(scope) != null;
}

export function saveScopedSlotPreference(
  scope: SlotPreferenceScope | undefined,
  input: SlotPreferenceInput
): SlotPreference | undefined {
  if (!hasCompleteScope(scope)) return undefined;

  const storage = getStorage();
  if (!storage) return undefined;

  const pref: ScopedSlotPreference = {
    ...input,
    savedAt: Date.now(),
    userId: scope.userId,
    vehicleId: String(scope.vehicleId)
  };

  try {
    storage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(pref));
    return pref;
  } catch {
    return undefined;
  }
}

export function clearScopedSlotPreference(): void {
  const storage = getStorage();
  storage?.removeItem(CHECKOUT_STORAGE_KEY);
}

export function isSlotPreferenceStale(slotPreference: SlotPreference | undefined): boolean {
  return slotPreference != null && Date.now() - slotPreference.savedAt > STALENESS_THRESHOLD_MS;
}
